import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import PhotoLightboxWithInfo from './PhotoLightboxWithInfo';
import ThumbnailImage from './ThumbnailImage';

const metaCache = new Map();

function prettyBytes(value) {
  if (value == null) return null;
  let size = Number(value);
  if (!Number.isFinite(size)) return null;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) { size /= 1024; unit += 1; }
  return size.toFixed(size < 10 ? 2 : 1) + ' ' + units[unit];
}

function loadMeta(url) {
  if (!metaCache.has(url)) {
    const dimensions = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.naturalWidth || null, height: img.naturalHeight || null });
      img.onerror = () => resolve({ width: null, height: null });
      img.referrerPolicy = 'no-referrer';
      img.src = url;
    });
    const size = fetch(url, { method: 'HEAD' })
      .then((res) => {
        const length = res.headers.get('Content-Length');
        return length && /^\d+$/.test(length) ? Number(length) : null;
      })
      .catch(() => null);
    metaCache.set(url, Promise.all([dimensions, size]).then(([dims, bytes]) => ({ ...dims, size: bytes })));
  }
  return metaCache.get(url);
}

function PhotoInfoLink({ photo, url, label }) {
  const [active, setActive] = useState(false);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!active || !url || meta) return undefined;
    let cancelled = false;
    loadMeta(url).then((result) => { if (!cancelled) setMeta(result); });
    return () => { cancelled = true; };
  }, [active, url, meta]);

  const parts = [];
  if (meta?.width && meta?.height) parts.push(meta.width + '×' + meta.height);
  if (meta?.size != null) parts.push(prettyBytes(meta.size));
  const text = meta ? (parts.join(' • ') || '—') : '…';

  return (
    <span
      className="record-photo__info"
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      onFocus={() => setActive(true)}
      onBlur={() => setActive(false)}
    >
      <Link to={'/photos/' + photo.id}>{label}<ExternalLink size={13} /></Link>
      {active && <span className="record-photo__meta" role="tooltip">{text}</span>}
    </span>
  );
}

export default function RecordPhotoGrid({ photos = [], labels }) {
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
  const withHost = (value) => (value?.startsWith('/') ? uploadHost + value : value);

  if (!photos.length) return <p className="record-empty">{labels.empty}</p>;

  return (
    <>
      <div className="record-photo-grid">
        {photos.map((photo, index) => {
          const src = withHost(photo.thumbnailUrl || photo.url);
          return (
            <figure className="record-photo" key={photo.id || src}>
              <button type="button" onClick={() => setLightboxIndex(index)} aria-label={labels.open + ' ' + (index + 1)}>
                <ThumbnailImage src={src} alt="" />
              </button>
              <figcaption>
                <span>№ {photo.id}</span>
                <PhotoInfoLink photo={photo} url={withHost(photo.url)} label={labels.info} />
              </figcaption>
            </figure>
          );
        })}
      </div>
      <PhotoLightboxWithInfo
        photos={photos}
        open={lightboxIndex >= 0}
        index={Math.max(lightboxIndex, 0)}
        onClose={() => setLightboxIndex(-1)}
        onIndexChange={setLightboxIndex}
      />
    </>
  );
}
